import { useState, useEffect } from 'react';

// Enhanced auth hook that works with both authenticated and anonymous users
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Check current authentication status
  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    
    // Handle OAuth callback results
    const urlParams = new URLSearchParams(window.location.search);
    const authResult = urlParams.get('auth');
    
    if (authResult === 'success') {
      // OAuth was successful, refresh user data
      setTimeout(() => {
        checkAuthStatus();
        // Clear the URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    } else if (authResult === 'error') {
      const errorMessage = urlParams.get('message');
      console.error('OAuth error:', errorMessage);
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('🔵 Auth hook: signInWithGoogle called');
      console.log('🔵 Making request to /api/auth/google');
      
      const response = await fetch('/api/auth/google', {
        credentials: 'include'
      });
      
      console.log('🔵 Auth hook: Response status:', response.status);
      console.log('🔵 Auth hook: Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔵 Auth hook: Response data:', data);
        
        // Store anonymous user ID before redirect (for potential migration)
        const anonymousUserId = localStorage.getItem('cf_user_id');
        if (anonymousUserId) {
          sessionStorage.setItem('migrate_from_user', anonymousUserId);
        }
        // Redirect to Google OAuth
        console.log('🔵 Auth hook: Redirecting to:', data.authUrl);
        window.location.href = data.authUrl;
      } else {
        const errorText = await response.text();
        console.error('🔵 Auth hook: Error response:', errorText);
        throw new Error(`Failed to initiate Google sign-in: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('🔵 Auth hook: Error:', error);
      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      const response = await fetch('/api/auth/apple', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store anonymous user ID before redirect (for potential migration)
        const anonymousUserId = localStorage.getItem('cf_user_id');
        if (anonymousUserId) {
          sessionStorage.setItem('migrate_from_user', anonymousUserId);
        }
        // Redirect to Apple OAuth
        window.location.href = data.authUrl;
      } else {
        throw new Error('Failed to initiate Apple sign-in');
      }
    } catch (error) {
      console.error('Apple sign-in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setUser(null);
        // Generate new anonymous user ID
        const newAnonymousId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('cf_user_id', newAnonymousId);
        // Reload page to reset app state
        window.location.reload();
      } else {
        throw new Error('Failed to sign out');
      }
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const migrateAnonymousData = async () => {
    const anonymousUserId = sessionStorage.getItem('migrate_from_user');
    if (!anonymousUserId || !user) {
      return false;
    }
    
    try {
      const response = await fetch('/api/auth/migrate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ anonymousUserId })
      });
      
      if (response.ok) {
        // Clear migration flag
        sessionStorage.removeItem('migrate_from_user');
        // Update localStorage to use authenticated user ID
        localStorage.setItem('cf_user_id', user.id);
        return true;
      } else {
        throw new Error('Failed to migrate anonymous data');
      }
    } catch (error) {
      console.error('Migration error:', error);
      return false;
    }
  };

  // Check if user has anonymous data that can be migrated
  const hasMigratableData = () => {
    const anonymousUserId = sessionStorage.getItem('migrate_from_user');
    return !!anonymousUserId && !!user && user.provider !== 'anonymous';
  };

  const getUserId = () => {
    if (user && user.id) {
      return user.id;
    }
    // Fallback to anonymous ID
    let userId = localStorage.getItem('cf_user_id');
    if (!userId) {
      userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cf_user_id', userId);
    }
    return userId;
  };

  const getHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // If user is authenticated, use cookies; otherwise use header
    if (!user) {
      headers['X-User-ID'] = getUserId();
    }
    
    return headers;
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    const config = {
      ...options,
      headers: {
        ...getHeaders(),
        ...options.headers
      }
    };
    
    // Include cookies for authenticated users
    if (user) {
      config.credentials = 'include';
    }
    
    return fetch(url, config);
  };

  return {
    user,
    loading,
    initialized,
    isAuthenticated: !!user && user.provider !== 'anonymous',
    isAnonymous: !user || user.provider === 'anonymous',
    signInWithGoogle,
    signInWithApple,
    signOut,
    checkAuthStatus,
    migrateAnonymousData,
    hasMigratableData,
    getUserId,
    getHeaders,
    makeAuthenticatedRequest
  };
};